import { BACKSTOP_ROLE_ID } from "@/features/rbac/permissions/systemRoles";
import type { RbacAssignment, RbacRole } from "@/features/rbac/schemas/rbac";
import type { RbacSubject } from "@/features/rbac/services/subjectResolver";

/** De dónde salieron los roles efectivos de una persona. */
export type PermissionSource = "assignment" | "legacy" | "none";

export interface EffectivePermissions {
  /** Permisos y comodines concedidos, sin expandir. */
  readonly permissions: ReadonlySet<string>;
  /** Roles que se aplicaron realmente (los ids desconocidos quedan fuera). */
  readonly roleIds: readonly string[];
  readonly source: PermissionSource;
}

const EMPTY: EffectivePermissions = {
  permissions: new Set<string>(),
  roleIds: [],
  source: "none",
};

function unionOf(
  roleIds: readonly string[],
  rolesById: ReadonlyMap<string, RbacRole>
): { permissions: Set<string>; applied: string[] } {
  const permissions = new Set<string>();
  const applied: string[] = [];

  for (const roleId of roleIds) {
    const role = rolesById.get(roleId);
    // Un id desconocido se ignora: si alguien borra un rol, sus portadores
    // pierden esos permisos en vez de romper la sesión.
    if (!role) continue;

    applied.push(roleId);
    for (const permission of role.permissions) permissions.add(permission);
  }

  return { permissions, applied };
}

/**
 * Calcula el conjunto efectivo de permisos de una persona.
 *
 * Precedencia:
 *
 * 1. Si tiene asignación con roles, gana la asignación.
 * 2. Si no, se cae al camino heredado: los valores de `permission_admin` SON
 *    los ids de los roles del sistema, así que se usan tal cual; y un
 *    `type === "admin"` añade el rol de respaldo.
 * 3. Si no hay nada, conjunto vacío.
 *
 * No hay día de corte: quien nunca se migre sigue funcionando, y editar un rol
 * del sistema desde la UI afecta de inmediato a los usuarios heredados que lo
 * llevan.
 */
export function resolveEffectivePermissions(
  subject: RbacSubject | null,
  assignment: RbacAssignment | null,
  roles: readonly RbacRole[]
): EffectivePermissions {
  if (!subject) return EMPTY;

  const rolesById = new Map(roles.map((role) => [role.id, role]));

  if (assignment && assignment.roleIds.length > 0) {
    const { permissions, applied } = unionOf(assignment.roleIds, rolesById);
    return { permissions, roleIds: applied, source: "assignment" };
  }

  const legacyRoleIds = [...subject.legacyPermissions];
  if (subject.legacyType === "admin" && !legacyRoleIds.includes(BACKSTOP_ROLE_ID)) {
    legacyRoleIds.push(BACKSTOP_ROLE_ID);
  }

  if (legacyRoleIds.length === 0) return EMPTY;

  const { permissions, applied } = unionOf(legacyRoleIds, rolesById);
  if (applied.length === 0) return EMPTY;

  return { permissions, roleIds: applied, source: "legacy" };
}
